import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RISK_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RISKS = ["verde", "amarelo", "laranja", "vermelho"];

/** Edição de monitoramentos — PGA (grãos) e Salas Elétricas usam o mesmo formulário. */
export default function MonitoringEditDialog({ open, onOpenChange, kind = "pga", record, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open || !record) return;
    setForm({
      notes: record.notes || "",
      finished_at: (record.finished_at || "").slice(0, 16),
      temperature: record.temperature ?? "",
      humidity: record.humidity ?? "",
      risk_level: record.risk_level || "",
      risk_reason: "",
    });
  }, [open, record]);

  if (!form || !record) return null;

  const isPga = kind === "pga";
  const url = isPga ? `/pga/monitorings/${record.id}` : `/electric-rooms/inspections/${record.id}`;
  const label = isPga ? record.structure_name : record.room_name;

  const save = async () => {
    setBusy(true);
    try {
      const payload = isPga
        ? {
            notes: form.notes,
            finished_at: form.finished_at ? `${form.finished_at}:00` : null,
            temperature: form.temperature === "" ? null : parseFloat(form.temperature),
            humidity: form.humidity === "" ? null : parseFloat(form.humidity),
          }
        : {
            notes: form.notes,
            finished_at: form.finished_at ? `${form.finished_at}:00` : null,
            risk_level: form.risk_level || null,
            risk_reason: form.risk_reason,
          };
      await api.put(url, payload);
      toast.success("Monitoramento atualizado");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="monitoring-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Editar monitoramento — {label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Data e hora do monitoramento</Label>
            <Input type="datetime-local" value={form.finished_at} onChange={(e) => set({ finished_at: e.target.value })}
              className="h-9" data-testid="monitoring-date-input" />
          </div>

          {isPga && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Temperatura (°C)</Label>
                <Input type="number" step="0.1" value={form.temperature} onChange={(e) => set({ temperature: e.target.value })}
                  className="h-9" data-testid="monitoring-temp-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Umidade (%)</Label>
                <Input type="number" step="0.1" value={form.humidity} onChange={(e) => set({ humidity: e.target.value })}
                  className="h-9" data-testid="monitoring-humidity-input" />
              </div>
            </div>
          )}

          {!isPga && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Nível de risco</Label>
                <Select value={form.risk_level} onValueChange={(v) => set({ risk_level: v })}>
                  <SelectTrigger className="h-9" data-testid="monitoring-risk-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{RISKS.map((r) => <SelectItem key={r} value={r}>{RISK_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.risk_level && form.risk_level !== record.risk_level && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Justificativa da alteração do risco *</Label>
                  <Input value={form.risk_reason} onChange={(e) => set({ risk_reason: e.target.value })}
                    placeholder="Por que o risco calculado foi ajustado?" className="h-9" data-testid="monitoring-risk-reason-input" />
                </div>
              )}
            </>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Observações</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })}
              className="resize-none" data-testid="monitoring-notes-input" />
          </div>

          <p className="text-xs text-muted-foreground">
            Checklist, capturas e evidências fotográficas são preservados. As alterações ficam registradas na auditoria.
          </p>
          <Button className="w-full" onClick={save} disabled={busy} data-testid="monitoring-edit-save-btn">
            {busy ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
